import React, { lazy, Suspense } from 'react';
import SkeletonUserInfoLayout from 'src/components/skeleton/SkeletonUserInfoLayout';
import SkeletonVNTracking from 'src/components/skeleton/SkeletonVNTracking';
import { Path } from 'src/contents/Constants';

import NothingLayout from 'src/layouts/NothingLayout';
import OutSideLayout from 'src/layouts/OutSideLayout';
import MainLayout from 'src/layouts/MainLayout';
import UserInfoLayout from 'src/layouts/UserInfoLayout';

const CatalogDetail = lazy(() => import('src/pages/catalog-detail'));
const Quotation = lazy(() => import('src/pages/quotation'));
const QuotationDeal = lazy(() => import('src/pages/quotation-deal'));
const Complete = lazy(() => import('src/pages/complete'));
const Cart = lazy(() => import('src/pages/cart'));
const Privacy = lazy(() => import('src/pages/privacy'));
const DebtRecovery = lazy(() => import('src/pages/debt-recovery'));

const OrderConfirm = lazy(() => import('src/pages/order-confirm'));
const OrderManagement = lazy(() => import('src/pages/order-management'));
const OrderManagementDetail = lazy(() =>
  import('src/pages/order-management-detail')
);
const OrderManagementJourney = lazy(() =>
  import('src/pages/order-management-journey')
);
const VNTracking = lazy(() => import('src/pages/vn-tracking'));
const VNTrackingDetail = lazy(() => import('src/pages/vn-tracking-detail'));
const UserProfile = lazy(() => import('src/pages/user-profile'));
const UserInfo = lazy(() => import('src/pages/user-info'));
const ChangePassword = lazy(() => import('src/pages/change-password'));
const AddressManagement = lazy(() => import('src/pages/address-management'));
const Notification = lazy(() => import('src/pages/notification'));
const Referral = lazy(() => import('src/pages/referral'));
const Welcome = lazy(() => import('src/pages/welcome'));

const SignUp = lazy(() => import('src/pages/sign-up'));
const SignIn = lazy(() => import('src/pages/sign-in'));
const Identify = lazy(() => import('src/pages/identify'));
const Recovery = lazy(() => import('src/pages/recovery'));
const VerifyEmail = lazy(() => import('src/pages/verify-email'));
const AutoLogin = lazy(() => import('src/pages/auto-login'));
const ValidateReferral = lazy(() => import('src/pages/referral/ValidateReferral'));

const RoutePages = {
  normal: [
    {
      path: Path.VERIFY_EMAIL,
      Layout: NothingLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <VerifyEmail />
        </Suspense>
      )
    },
    {
      path: Path.QUOTATION,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <Quotation />
        </Suspense>
      )
    },

    {
      path: Path.SHOPPING_CART,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <Cart />
        </Suspense>
      )
    },
    {
      path: Path.PRIVACY_POLICY,
      Layout: ({ children }) => children,
      Page: () => (
        <Suspense fallback={<div />}>
          <Privacy />
        </Suspense>
      )
    },
    {
      path: Path.DEBT_RECOVERY,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <DebtRecovery />
        </Suspense>
      )
    },
    {
      path: Path.PRODUCT_DETAIL,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <CatalogDetail />
        </Suspense>
      )
    },
    {
      path: '/quotation/:dealId',
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <QuotationDeal />
        </Suspense>
      )
    }
  ],
  private: [
    {
      path: Path.SHOPPING_ORDER,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <OrderConfirm />
        </Suspense>
      )
    },
    {
      path: Path.ORDER_MANAGEMENT,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <OrderManagement />
        </Suspense>
      )
    },
    {
      path: Path.TRACKING_ORDER_DETAIL,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <OrderManagementDetail />
        </Suspense>
      )
    },
    {
      path: Path.ORDER_MANAGEMENT_DETAIL_TRACKING,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <OrderManagementJourney />
        </Suspense>
      )
    },
    {
      path: Path.LIST_VN_TRACKING,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonVNTracking />}>
          <VNTracking />
        </Suspense>
      )
    },
    {
      path: Path.DETAIL_VN_TRACKING,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <VNTrackingDetail />
        </Suspense>
      )
    },
    {
      path: Path.USER_PROFILE,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonUserInfoLayout />}>
          <UserProfile />
        </Suspense>
      )
    },
    {
      path: Path.USER_INFO,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonUserInfoLayout />}>
          <UserInfo />
        </Suspense>
      )
    },
    {
      path: Path.CHANGE_PASSWORD,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonUserInfoLayout />}>
          <ChangePassword />
        </Suspense>
      )
    },
    {
      path: Path.USER_ADDRESS,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonUserInfoLayout />}>
          <AddressManagement />
        </Suspense>
      )
    },
    {
      path: Path.NOTIFICATION,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonUserInfoLayout />}>
          <Notification />
        </Suspense>
      )
    },
    {
      path: Path.USER_REFERRAL,
      Layout: UserInfoLayout,
      Page: () => (
        <Suspense fallback={<SkeletonUserInfoLayout />}>
          <Referral />
        </Suspense>
      )
    },
    {
      path: Path.WELCOME,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <Welcome />
        </Suspense>
      )
    },
    {
      path: Path.SHOPPING_COMPLETE,
      Layout: MainLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <Complete />
        </Suspense>
      )
    }
  ],
  public: [
    {
      path: Path.SIGN_UP,
      Layout: OutSideLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <SignUp />
        </Suspense>
      )
    },
    {
      path: Path.SIGN_IN,
      Layout: OutSideLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <SignIn />
        </Suspense>
      )
    },
    {
      path: Path.IDENTIFY,
      Layout: NothingLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <Identify />
        </Suspense>
      )
    },
    {
      path: Path.RECOVERY,
      Layout: NothingLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <Recovery />
        </Suspense>
      )
    },
    {
      path: Path.RECOVERY_EMAIL,
      Layout: NothingLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <VerifyEmail />
        </Suspense>
      )
    },
    {
      path: Path.VERIFY_EMAIL_CONFIRMED,
      Layout: NothingLayout,
      Page: () => (
        <Suspense fallback={<div />}>
          <AutoLogin />
        </Suspense>
      )
    },
    {
      path: Path.REFERRAL,
      Layout: ({ children }) => children,
      Page: () => (
        <Suspense fallback={<div />}>
          <ValidateReferral />
        </Suspense>
      )
    }
  ]
};

export default RoutePages;
